import {type CustomRunNames} from '../../state/views/customRunNames/types';
import type {Ref as PanelRef} from '../../state/views/panel/types';
import {type Expression} from '../../util/expr';
import {type PanelProps} from '../../util/panelProps';
import type {PanelWithConfig} from '../../util/panelTypes';
import {type ChartAreaOption} from '../../util/plotHelpers/chartTypes';
import {type LegendPosition} from '../../util/plotHelpers/legendTypes';
import {type PlotFontSizeOrAuto} from '../../util/plotHelpers/plotFontSize';
import {
  type AggregateCalculation,
  type Line,
  type Mark,
  type PlotType,
  type RunSetInfo,
  type Timestep,
} from '../../util/plotHelpers/types';
import {type RunSetQuery} from '../../util/queryTypes';
import {type Key} from '../../util/runTypes';
import {type RunColorConfig} from '../../util/section';
import {type TruncationType} from '../common/TruncateText/TruncateTextTypes';
import {
  type ExcludeOutliersValues,
  type FullFidelityMode,
  type PointVisualizationOptions,
  type SemanticLegendSettingsWithCategoricalConfig,
  type SinglePointRenderMode,
  type TooltipNumberOfRunsOptions,
} from '../WorkspaceDrawer/Settings/types';
import {
  type ChartAggOption,
  type SmoothingType,
  type XAxisFormatValues,
} from './../../generated/graphql';
import {type RunHistoryRow} from './../../types/run';
import {type HistoryRecord} from './componentOutliers/types';
import {type LinePlotRun} from './linePlotRun';

export const PANEL_TYPE = 'Run History Line Plot';

export type HistoryPoint = RunHistoryRow;

export interface LinePlotPanel extends PanelWithConfig<typeof PANEL_TYPE> {
  ref: PanelRef;
}

export type RunsLinePlotPanelProps = PanelProps<RunsLinePlotConfig>;

export type RunsLinePlotPanelInnerProps = RunsLinePlotPanelProps & {
  lines: Line[];
  lineCount: number;
  runNameTruncationType?: TruncationType;
};

/**
 * W&B-admin-only override for which delta query family a line plot panel uses.
 * Undefined (the default) means "let getQueryType decide". A saved value only
 * takes effect for admins — see the gating in PanelConfigContext.
 */
export type DeltaQueryTypeOverride = 'bucketed' | 'backendLines';

/**
 * This is a comprehensive bucket of all the editable options for a PanelRunsLinePlot. Each value is optional because a panel isn't required to have any or all of these. Rather, each subcomponent of the panel editor will choose its own default value.
 */
export interface RunsLinePlotConfig {
  // basic config
  chartTitle?: string;
  coordinateZooming?: boolean;
  expressions?: string[];
  fontSize?: PlotFontSizeOrAuto;
  /**
   * Notes on exclude outliers: https://weightsandbiases.slack.com/archives/C062UKHTAHG/p1730937336547109
   */
  excludeOutliers?: ExcludeOutliersValues;

  limit?: number; // max number of runs or groups to show
  metricLimit?: number; // max metrics per run; total lines = metricLimit * limit (capped by maxRunsLimit)
  plotType?: PlotType;
  singlePointRenderMode?: SinglePointRenderMode; // How we show single point runs
  pointVisualizationMethod?: PointVisualizationOptions;
  fullFidelityMode?: FullFidelityMode;
  // Grouped-only opt-in; a new key so a pre-existing fullFidelityMode cannot flip a grouped chart.
  groupedFullFidelityMode?: FullFidelityMode;
  tooltipNumberOfRuns?: TooltipNumberOfRunsOptions;

  // axis options
  startingXAxis?: string;
  xAxis?: string;
  xAxisFormat?: XAxisFormatValues;
  xAxisMax?: number | null;
  xAxisMin?: number | null;
  xAxisTitle?: string;
  xExpression?: string; // expression for x axis
  xLogScale?: boolean;
  yAxisMax?: number | null;
  yAxisMin?: number | null;
  yAxisTitle?: string;
  yLogScale?: boolean;

  // grouping
  aggregate?: boolean; // user selected grouping
  groupAgg?: ChartAggOption;
  groupArea?: ChartAreaOption;
  groupRunsLimit?: number;
  groupingDepth?: number;
  groupBy?: string; // key to group by
  isGrouped?: boolean;
  useRunsTableGroupingInPanels?: boolean; // respect the runs table grouping in the chart
  windowing?: boolean; // windowing groups data into common x-axis buckets, false here will try to combine w/out aggregating

  // legend
  legendFields?: string[];
  legendPosition?: LegendPosition;
  legendTemplate?: string; // used to generate the default legend
  showLegend?: boolean; // Display legend in chart (default true)

  /** Whether run names should be colored in panel tooltips */
  colorRunNames?: boolean;

  /** if true, only show the highlighted run in companion plots */
  highlightedCompanionRunOnly?: boolean;

  /** "no crop" option for chart legends on the primary chart to accommodate long run names with crucial information in the middle */
  displayFullRunName?: boolean;

  // metrics
  aggregateMetrics?: boolean; // aggregate metrics into single metric
  colorEachMetricDifferently?: boolean; // if we have multiple metrics, override the run colors
  metrics?: string[]; // names of yAxis metrics
  metricRegex?: string; // can choose yAxis metrics by regex
  metricRegexMaxNumMatches?: number;
  useMetricRegex?: boolean;
  isMergedPanel?: boolean; // if true, other panels matching metricRegex should be removed from panel bank

  // Overrides
  overrideColors?: {[key: string]: {color: string; transparentColor: string}};
  overrideMarks?: {[key: string]: Mark};
  overrideLineWidths?: {[key: string]: number};
  overrideSeriesTitles?: {[key: string]: string}; // For setting the specific names of lines

  // smoothing
  smoothingWeight?: number;
  smoothingType?: SmoothingType;
  showOriginalAfterSmoothing?: boolean; // Show the line and the smoothed line
  useLocalSmoothing?: boolean;
  useGlobalSmoothingWeight?: boolean;

  // crosshair
  showXRange?: boolean;

  // force react-vis (W&B ADMIN ONLY)
  forceReactVis?: boolean;

  // delta query type override (W&B ADMIN ONLY)
  deltaQueryTypeOverride?: DeltaQueryTypeOverride;
}

export type RunsLinePlotConfigDefaults = Required<
  Pick<
    RunsLinePlotConfig,
    | 'aggregateMetrics'
    | 'legendFields'
    | 'groupAgg'
    | 'groupArea'
    | 'metrics'
    | 'plotType'
    | 'showOriginalAfterSmoothing'
    | 'smoothingType'
    | 'smoothingWeight'
    | 'useRunsTableGroupingInPanels'
    | 'windowing'
  >
>;

export type LinesFromDataProps = {
  aggregateMetrics: boolean;
  aggregatePanelRuns: boolean;
  aggregateCalculations: AggregateCalculation[];
  colorEachMetricDifferently: boolean;
  customRunColors?: RunColorConfig;
  customRunNames?: CustomRunNames;
  entityName?: string;
  expressions?: Expression[];
  groupArea: AggregateCalculation;
  groupLine: ChartAggOption;
  groupingDepth?: number;
  legendFields?: string[];
  getLegendTemplateByRunsetId: (runsetId: string | undefined) => string;
  groupKeysByRunsetId: Record<string, Key[]>;
  plotType?: PlotType;
  projectName?: string;
  runSets?: RunSetInfo[];
  semanticLegendSettings?: SemanticLegendSettingsWithCategoricalConfig;
  showOriginalAfterSmoothing: boolean;
  singleRun: boolean;
  smoothingParam: number; // This maps to config.smoothingWeight
  xAxis: string;
  xAxisFormat?: XAxisFormatValues;
  xExpression?: Expression;
  yAxis: string[];
  yLogScale?: boolean;
  zoomTimestep: Timestep | null;
} & Required<
  Pick<
    RunsLinePlotConfig,
    'windowing' | 'limit' | 'smoothingType' | 'useRunsTableGroupingInPanels'
  >
>;

export type BucketedLine = {
  history: Array<Array<HistoryRecord>>;
  runsetInfo?: RunSetQuery | undefined;
  // TODO: This is never actually queried, but to avoid changing existing behavior, I'm making this optional for now so it
  // at least has the correct type.
  runsetId?: string;
  groupTotalRuns?: number;
  groupIncludedRuns?: number;
} & LinePlotRun;

export type BucketedData = {
  data: {
    _dataType: 'bucketing-gorilla';
    allRunsetsHaveBackendLines?: boolean;
    entityName: string;
    fetchedRunCount?: number;
    fetchedRunCountByRunsetId?: Record<string, number>;
    hasSubsampledGroups?: boolean;
    histories: {
      data: BucketedLine[];
    };
    initialLoading: boolean;
    loadMore: () => void;
    projectName: string;
    totalGroupedIncludedRuns?: number;
  };
  loading: boolean;
};

export type FullZoom = {
  xAxisMin: number;
  xAxisMax: number;
  yAxisMin: number;
  yAxisMax: number;
};
export type PartialZoom = Partial<FullZoom>;
export type AxisZoomIntent = {
  shouldZoomX: boolean;
  shouldZoomY: boolean;
};
export type Zoom = PartialZoom;

/**
 * Transient command payload for user-driven zoom gestures. `intent` is the
 * source of truth when present: bounds on axes whose intent is false are ignored
 * so one-axis zooms preserve the current bounds on the opposing axis.
 */
export type UserZoomIntentPayload = PartialZoom & {
  intent?: AxisZoomIntent;
};

/** ConfigZoomUpdate is the write-path counterpart to `Zoom`. Accepts `null` on
 *  range fields to represent an explicitly cleared value. */
export type ConfigZoomUpdate = {
  [K in keyof FullZoom]?: number | null;
};
